import React, { useState, useCallback } from 'react';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import { parseAnkiQuestion } from '@/utils/anki-utils';
import { supabase } from '@/lib/supabase';
import type { ExamCategory, ExamSection } from '@/types/exam';
import { categorizeQuestionDifficulty } from '@/utils/questionCategorization';

interface MockTestAnkiImportProps {
  selectedExam?: ExamCategory | null;
  selectedSection?: ExamSection | null;
  onImport?: () => void;
}

const MockTestAnkiImport: React.FC<MockTestAnkiImportProps> = ({ 
  selectedExam,
  selectedSection,
  onImport 
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [previewQuestions, setPreviewQuestions] = useState<any[]>([]);
  const [isCompletePaper, setIsCompletePaper] = useState(false);
  const [examName, setExamName] = useState(selectedExam?.name || '');
  const [section, setSection] = useState(selectedSection?.name || '');
  const [showPreview, setShowPreview] = useState(false);

  React.useEffect(() => {
    if (selectedExam) {
      setExamName(selectedExam.name);
    }
    if (selectedSection) {
      setSection(selectedSection.name);
    }
  }, [selectedExam, selectedSection]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      parseAnkiFile(selectedFile);
    }
  };

  const parseAnkiFile = useCallback(async (file: File) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n').filter(line => line.trim() !== '');
      
      const parsedQuestions = [];
      for (const line of lines) {
        const parsed = parseAnkiQuestion(line, examName);
        if (parsed) {
          parsedQuestions.push(parsed);
        }
      }
      setPreviewQuestions(parsedQuestions);
    };
    reader.readAsText(file);
  }, [examName]);

  const determineDifficulty = (questionText: string): 'easy' | 'medium' | 'hard' => {
    if (questionText.includes('\n') || questionText.length > 150) {
      return 'hard';
    } else if (questionText.length > 80) {
      return 'medium';
    } else {
      return 'easy';
    }
  };

  const showPreviewDialog = () => {
    if (!examName) {
      toast.error("Please select an exam name");
      return;
    }
    if (!section && !isCompletePaper) {
      toast.error("Please select a section name or mark as complete paper");
      return;
    }
    if (previewQuestions.length === 0) {
      toast.error("No questions to preview");
      return;
    }

    setShowPreview(true);
  };

  const importQuestions = async () => {
    if (!examName) {
      toast.error("Please select an exam category");
      return;
    }

    setIsImporting(true);
    try {
      const questions = previewQuestions.map(q => ({
        exam_category: selectedExam?.type || 'entrance',
        exam_name: examName,
        category: section || examName,
        question_text: q.data.Question,
        option_a: q.data["Option A"],
        option_b: q.data["Option B"],
        option_c: q.data["Option C"],
        option_d: q.data["Option D"],
        correct_option: q.data["Correct Answer"],
        explanation: q.data.Explanation,
        difficulty: determineDifficulty(q.data.Question),
        metadata: {
          is_complete_paper: isCompletePaper,
          original_section: section,
          exam_name: examName,
          set_name: "Anki Import"
        }
      }));

      const { error } = await supabase
        .from('mock_test_questions')
        .insert(questions);

      if (error) throw error;

      toast.success(`Successfully imported ${questions.length} questions`);
      if (onImport) onImport();
      setFile(null);
      setPreviewQuestions([]);
      setShowPreview(false);
    } catch (error) {
      console.error('Error importing questions:', error);
      toast.error("Failed to import questions");
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Card className="p-4">
      <div className="space-y-4">
        <div className="flex items-center gap-4 mb-4">
          <input
            type="file"
            accept=".txt"
            onChange={handleFileChange}
            className="hidden"
            id="mock-anki-file"
          />
          <Button
            variant="outline"
            onClick={() => document.getElementById('mock-anki-file')?.click()}
          >
            <Upload className="h-4 w-4 mr-2" />
            Select Anki File
          </Button>
          {file && <span className="text-sm text-gray-600">{file.name}</span>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Exam Name</label>
            <input
              type="text"
              value={examName}
              onChange={(e) => setExamName(e.target.value)}
              className="w-full rounded-md border px-3 py-2"
              placeholder="Enter exam name"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Section Name</label>
            <input
              type="text"
              value={section}
              onChange={(e) => setSection(e.target.value)}
              className="w-full rounded-md border px-3 py-2"
              placeholder="Enter section name"
              disabled={isCompletePaper}
            />
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="isCompletePaper"
            checked={isCompletePaper}
            onCheckedChange={(checked) => {
              setIsCompletePaper(checked as boolean);
              if (checked) {
                setSection('');
              }
            }}
          />
          <label
            htmlFor="isCompletePaper"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Import as Complete Paper (No sections)
          </label>
        </div>

        {previewQuestions.length > 0 && (
          <div className="space-y-4">
            <h4 className="font-medium">Found {previewQuestions.length} questions</h4>
            
            <div className="flex space-x-4">
              <Button 
                onClick={showPreviewDialog}
                variant="outline"
              >
                Preview Questions
              </Button>

              <Button 
                onClick={importQuestions}
                disabled={isImporting}
              >
                {isImporting ? 'Importing...' : `Import ${previewQuestions.length} Questions`}
              </Button>
            </div>
          </div>
        )}
      </div>

      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Preview Questions</DialogTitle>
            <DialogDescription>
              Exam: {examName} {isCompletePaper ? '(Complete Paper)' : `- Section: ${section}`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-6">
            {previewQuestions.slice(0, 50).map((q, idx) => (
              <div key={idx} className="border rounded-md p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="font-medium">Question {idx + 1}</span>
                  <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                    {determineDifficulty(q.data.Question)}
                  </span>
                </div>
                <p>{q.data.Question}</p>
                <div className="space-y-1 ml-4">
                  <div className="flex items-start">
                    <span className="font-medium mr-2 min-w-[20px]">A:</span>
                    <p>{q.data["Option A"]}</p>
                  </div>
                  <div className="flex items-start">
                    <span className="font-medium mr-2 min-w-[20px]">B:</span>
                    <p>{q.data["Option B"]}</p>
                  </div>
                  <div className="flex items-start">
                    <span className="font-medium mr-2 min-w-[20px]">C:</span>
                    <p>{q.data["Option C"]}</p>
                  </div>
                  <div className="flex items-start">
                    <span className="font-medium mr-2 min-w-[20px]">D:</span>
                    <p>{q.data["Option D"]}</p>
                  </div>
                </div>
                <div className="bg-green-50 p-2 rounded border border-green-100">
                  <span className="font-medium">Correct Answer:</span> {q.data["Correct Answer"]}
                </div>
              </div>
            ))}
            {previewQuestions.length > 50 && (
              <p className="text-center text-gray-500">
                Showing first 50 out of {previewQuestions.length} questions
              </p>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Cancel
            </Button>
            <Button onClick={importQuestions} disabled={isImporting}>
              {isImporting ? 'Importing...' : `Import ${previewQuestions.length} Questions`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default MockTestAnkiImport;
